import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Divider
} from "@chakra-ui/react";
import { FaTint, FaArrowRight, FaCheckCircle, FaTools, FaFlask } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterFiltersUnderSink: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/water-filters/under-sink-system"
        fallbackTitle="Under Sink Water Filter — Professional Installation — One Water Australia"
        fallbackDescription="Under-sink water filters professionally specified and installed for Australian homes. The right filter for your specific water supply."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 16, md: 24 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1585771724684-38269d6639fd?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" maxW="3xl" mx="auto">
            <Icon as={FaTint} boxSize={12} color={WATER_BLUE} />
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Clean, filtered water from your kitchen tap.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.800">
              Professionally specified, installed, and maintained long-term.
            </Text>
            <Text fontSize="md" color="whiteAlpha.700" maxW="xl">
              The word "professionally" separates this from a DIY Bunnings kit. The right filter for your water, not a generic off-the-shelf product.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* What it actually does */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={6}>
            <Heading size="xl" color={NAVY}>What an under-sink filter actually does</Heading>
            <Text color="gray.600" fontSize="lg">
              An under-sink filter connects to your cold water line beneath your kitchen sink and treats all water from that tap — drinking, cooking, filling the kettle, making ice.
            </Text>
            <Text color="gray.600">
              Unlike a jug filter, which treats water after the fact, an under-sink filter treats everything before it reaches the glass. It filters ALL water from that tap — there's no forgetting to fill the jug.
            </Text>
            <Box borderLeft={`3px solid ${WATER_BLUE}`} pl={4}>
              <Text color="gray.600" fontStyle="italic">
                "You've been doing the right thing. A dedicated system simply does it better."
              </Text>
            </Box>
          </VStack>
          <VStack spacing={5}>
            <Box borderRadius="xl" overflow="hidden" h="220px" w="full" shadow="md">
              <Box
                as="img"
                src="https://images.unsplash.com/photo-1585771724684-38269d6639fd?auto=format&fit=crop&w=800&q=80"
                alt="Under-sink water filter system professionally installed"
                w="full"
                h="full"
                objectFit="cover"
              />
            </Box>
            <Box p={8} bg={ICE_MIST} borderRadius="2xl" w="full">
              <Heading size="md" color={NAVY} mb={6}>Typical filtration stages</Heading>
              <VStack spacing={4} align="stretch">
                {[
                  { stage: "1", name: "Sediment Pre-Filter", desc: "Removes particles, rust, and debris to protect downstream stages." },
                  { stage: "2", name: "Activated Carbon", desc: "Removes chlorine, chloramines, taste, odour, and organic compounds." },
                  { stage: "3", name: "Sub-Micron Filter", desc: "Targets finer particles and some VOCs." },
                  { stage: "4", name: "Polish Stage", desc: "Final quality pass before the water reaches your tap." },
                ].map((s) => (
                  <HStack key={s.stage} spacing={4} align="flex-start">
                    <Box
                      w={8} h={8}
                      borderRadius="full"
                      bg={DEEP_AQUA}
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="sm"
                      fontWeight="bold"
                      flexShrink={0}
                    >
                      {s.stage}
                    </Box>
                    <Box>
                      <Text fontWeight="600" color={NAVY} fontSize="sm">{s.name}</Text>
                      <Text fontSize="sm" color="gray.600">{s.desc}</Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* DIY vs Professional */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={12} textAlign="center">
            <Heading size="xl" color={NAVY}>DIY vs Professional Installation</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              You can buy and install one yourself. But choosing the right system for your specific water makes the difference between filtering the right contaminants — or not.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            <Box p={8} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
              <Heading size="md" color="gray.500" mb={5}>DIY Install</Heading>
              <VStack align="flex-start" spacing={3}>
                {[
                  "Generic filter selected without water testing",
                  "May not address your specific supply issues",
                  "Installation errors can void warranties",
                  "Filter change intervals often missed",
                  "No follow-up water quality verification",
                ].map((item) => (
                  <HStack key={item} spacing={2} align="flex-start">
                    <Text color="gray.400" fontSize="sm" mt={0.5} flexShrink={0}>✕</Text>
                    <Text fontSize="sm" color="gray.600">{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
            <Box p={8} bg={ICE_MIST} borderRadius="xl" border={`2px solid ${WATER_BLUE}40`}>
              <Heading size="md" color={NAVY} mb={5}>One Water Professional</Heading>
              <VStack align="flex-start" spacing={3}>
                {[
                  "System specified for your actual water supply",
                  "Correct filter media targeting your contaminants",
                  "Professional installation with guaranteed workmanship",
                  "Scheduled maintenance reminders and service",
                  "UNSW lab testing for before/after verification",
                  "One team responsible from assessment to long-term support",
                ].map((item) => (
                  <HStack key={item} spacing={2} align="flex-start">
                    <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={4} mt={0.5} flexShrink={0} />
                    <Text fontSize="sm" color="gray.700">{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Benefits */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} mb={12} textAlign="center">
          <Heading size="xl" color={NAVY}>What You Get</Heading>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {[
            { icon: FaTint, title: "Better-Tasting Water", desc: "Chlorine and associated taste and odour removed at the source." },
            { icon: FaFlask, title: "Better Coffee & Cooking", desc: "Coffee, tea, and food cooked with filtered water taste noticeably different." },
            { icon: FaTools, title: "No Countertop Clutter", desc: "Installed under the sink. Clean kitchen bench, always available." },
            { icon: FaCheckCircle, title: "Zero Bottled Water", desc: "Reduce plastic, reduce cost, reduce carrying. Permanently." },
          ].map((benefit) => (
            <Box key={benefit.title} p={6} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="sm" textAlign="center">
              <Icon as={benefit.icon} boxSize={8} color={WATER_BLUE} mb={4} />
              <Heading size="sm" color={NAVY} mb={2}>{benefit.title}</Heading>
              <Text fontSize="sm" color="gray.600">{benefit.desc}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Social proof + trust */}
      <Box bg={ICE_MIST} py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {[
              { quote: "The difference in taste was immediate. We haven't bought bottled water since.", name: "Sarah M., Brisbane" },
              { quote: "Professional service from start to finish. They tested our water first, which gave us confidence in the system they recommended.", name: "James T., Gold Coast" },
              { quote: "Two years in, same team still maintaining it. Exactly what they promised.", name: "Renee K., Sunshine Coast" },
            ].map((t) => (
              <Box key={t.name} p={6} bg="white" borderRadius="xl" shadow="sm">
                <Text color="gray.600" fontStyle="italic" mb={4} fontSize="sm">"{t.quote}"</Text>
                <Text fontWeight="600" color={NAVY} fontSize="sm">— {t.name}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxW="container.xl" py={16}>
        <Box bg={NAVY} p={12} borderRadius="2xl" textAlign="center">
          <VStack spacing={5}>
            <Heading size="xl" color="white">Book a free water assessment.</Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="xl">
              We'll assess your supply, explain what we find, and recommend — or not recommend — a system. No obligation. No pressure.
            </Text>
            <HStack spacing={4} justify="center" flexWrap="wrap">
              <Button
                as={RouterLink}
                to="/contact"
                bg={WATER_BLUE}
                color={NAVY}
                size="lg"
                fontWeight="bold"
                _hover={{ bg: "#8DC5E0" }}
                transition="all 0.2s"
              >
                Book Free Assessment
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterFiltersUnderSink;
